import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function DesktopMenuSplit({
  side
}: {
  side: "left" | "right";
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2].map(i => <div key={i} data-index={i} className="h-4 w-16 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) return null;
  const orderMap: Record<string, number> = {
    "התפריט המעושן": 1,
    "הסיפור שלנו": 2,
    "דברו איתנו": 3
  };
  const sortedItems = [...menuItems].sort((a, b) => {
    return (orderMap[a.label] || 99) - (orderMap[b.label] || 99);
  });
  const mid = Math.ceil(sortedItems.length / 2);
  const items = side === "left" ? sortedItems.slice(0, mid) : sortedItems.slice(mid);
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {items.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium tracking-wide text-foreground/90 transition-colors hover:text-primary">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuOverlay({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 flex flex-col bg-background/95 backdrop-blur-xl transition-all duration-300 animate-in fade-in slide-in-from-top-5">
      <div className="flex items-center justify-end p-4">
        <Button variant="ghost" size="icon" onClick={onClose} className="text-foreground">
          <X className="h-6 w-6" />
        </Button>
      </div>
      <nav className="flex flex-col items-center justify-center gap-8 p-8">
        {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-bold text-foreground transition-colors hover:text-primary" onClick={onClose}>
            {item.label}
          </Link>)}
      </nav>
    </div>;
}
export default function TopNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="ניווט-עליון" dir="rtl" className={cn("dark sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300", isScrolled ? "bg-background/80 border-border/10 backdrop-blur-lg shadow-lg" : "bg-background")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-8">
        {}
        <div className="flex md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="text-foreground hover:bg-accent/20">
            <Menu className="h-6 w-6" />
          </Button>
        </div>

        {}
        <div className="flex shrink-0 items-center justify-center px-4 md:px-8">
          <Link to="/" className="block transition-opacity hover:opacity-90">
            <span className="text-xl font-black tracking-tighter text-foreground md:text-2xl">
              SMOKE & GO
            </span>
          </Link>
        </div>

        {}
        <div className="hidden flex-1 justify-center !w-[200px] md:flex">
          <MenuProvider menu_id="17" className="w-full flex justify-center">
            <DesktopMenuSplit side="left" />
          </MenuProvider>
        </div>

        {}
        <div className="hidden flex-1 justify-center md:flex">
          <MenuProvider menu_id="17" className="w-full flex justify-center">
            <DesktopMenuSplit side="right" />
          </MenuProvider>
        </div>

        {}
        <div className="w-10 md:hidden" />
      </div>

      {}
      <MenuProvider menu_id="17">
        <MobileMenuOverlay isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}